import React from 'react';
import PropTypes from 'prop-types';
import { Form, Popconfirm } from 'antd';
import { autobind } from 'core-decorators';
import moment from 'moment';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormTimePicker from 'components-desktop/FormTimePicker';
import FormInput from 'components-desktop/FormInput';
import FormInputNumber from 'components-desktop/FormInputNumber';
import FormSelect from 'components-desktop/FormSelect';
import FormSelect2 from 'components-desktop/FormSelect2';
import misc from 'misc/misc';
import helpers from 'misc/helpers';
import AskEffectiveDateDialog from './AskEffectiveDateDialog';

const propTypes = {
  form: PropTypes.object.isRequired,

  masterweek: PropTypes.object,
  caregivers: PropTypes.array.isRequired,
  contracts: PropTypes.array.isRequired,
  servicesByCompany: PropTypes.object.isRequired,

  // Callback methods to notify outside.
  onAddSuccess: PropTypes.func.isRequired,
  onAddCancel: PropTypes.func.isRequired,
  onDeleteSuccess: PropTypes.func.isRequired,
  onUpdateSuccess: PropTypes.func.isRequired,
};

@autobind
class PatientMasterweek extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      addingOrEditing: !props.masterweek,

      sunServices: [],
      monServices: [],
      tueServices: [],
      wedServices: [],
      thuServices: [],
      friServices: [],
      satServices: [],

      isAskEffectiveDateDialogVisible: false,
      onAskEffectiveDateDialogConfirmClick: null,
    };
  }

  componentDidMount() {
    const { masterweek } = this.props;

    this.id = this.parseQueryParam('id', this.props.location);

    // If adding, we can simply wait for onContractSelect to populate services.
    if (!this.isAdding()) {
      this.setState({
        sunServices: this.findContractServices(masterweek.getRemoteObject('sunday')?.getLong('contractId')),
        monServices: this.findContractServices(masterweek.getRemoteObject('monday')?.getLong('contractId')),
        tueServices: this.findContractServices(masterweek.getRemoteObject('tuesday')?.getLong('contractId')),
        wedServices: this.findContractServices(masterweek.getRemoteObject('wednesday')?.getLong('contractId')),
        thuServices: this.findContractServices(masterweek.getRemoteObject('thursday')?.getLong('contractId')),
        friServices: this.findContractServices(masterweek.getRemoteObject('friday')?.getLong('contractId')),
        satServices: this.findContractServices(masterweek.getRemoteObject('saturday')?.getLong('contractId')),
      });
    }
  }

  isAdding() {
    return !this.props.masterweek;
  }

  onEditClick() {
    this.setState({
      addingOrEditing: true,
    });
  }

  async onDeleteClick(record) {
    const { masterweek } = this.props;

    // Before posting, pop up a dialog to ask for the effective date.
    this.setState({
      isAskEffectiveDateDialogVisible: true,
      onAskEffectiveDateDialogConfirmClick: async (effectiveDate) => {
        if (moment(effectiveDate, 'MM/DD/YYYY').isValid()) {
          const params = {
            id: masterweek.getId(),
            effectiveDate: misc.toDateString(moment(effectiveDate, 'MM/DD/YYYY')),
          };
          await this.callService('/masterweek/delete', params);
          this.notifySuccess('Success', 'The masterweek has been deleted.');
          this.props.onDeleteSuccess();
        } else {
          this.notifyError('Error', 'Invalid effective date.');
        }
      },
    });
  }

  onSaveClick() {
    const { masterweek } = this.props;

    this.props.form.validateFieldsAndScroll(async (err, values) => {
      if (!err) {
        const params = {
          id: this.isAdding() ? undefined : masterweek.getId(),
          patientId: this.isAdding() ? this.id : undefined,
          startDate: helpers.extraFormDateValue(values, 'startDate'),
          endDate: helpers.extraFormDateValue(values, 'endDate'),

          sunFromTime: helpers.extraFormTimeValue(values, 'sunFromTime'),
          sunToTime: helpers.extraFormTimeValue(values, 'sunToTime'),
          sunBillHours: helpers.extraFormInputValue(values, 'sunBillHours'),
          sunCaregiverId: helpers.extraFormSelectValue(values, 'sunCaregiverId'),
          sunContractId: helpers.extraFormSelectValue(values, 'sunContractId'),
          sunServiceId: helpers.extraFormSelectValue(values, 'sunServiceId'),
          sunDisciplineRate: helpers.extraFormInputNumberValue(values, 'sunDisciplineRate'),

          monFromTime: helpers.extraFormTimeValue(values, 'monFromTime'),
          monToTime: helpers.extraFormTimeValue(values, 'monToTime'),
          monBillHours: helpers.extraFormInputValue(values, 'monBillHours'),
          monCaregiverId: helpers.extraFormSelectValue(values, 'monCaregiverId'),
          monContractId: helpers.extraFormSelectValue(values, 'monContractId'),
          monServiceId: helpers.extraFormSelectValue(values, 'monServiceId'),
          monDisciplineRate: helpers.extraFormInputNumberValue(values, 'monDisciplineRate'),

          tueFromTime: helpers.extraFormTimeValue(values, 'tueFromTime'),
          tueToTime: helpers.extraFormTimeValue(values, 'tueToTime'),
          tueBillHours: helpers.extraFormInputValue(values, 'tueBillHours'),
          tueCaregiverId: helpers.extraFormSelectValue(values, 'tueCaregiverId'),
          tueContractId: helpers.extraFormSelectValue(values, 'tueContractId'),
          tueServiceId: helpers.extraFormSelectValue(values, 'tueServiceId'),
          tueDisciplineRate: helpers.extraFormInputNumberValue(values, 'tueDisciplineRate'),

          wedFromTime: helpers.extraFormTimeValue(values, 'wedFromTime'),
          wedToTime: helpers.extraFormTimeValue(values, 'wedToTime'),
          wedBillHours: helpers.extraFormInputValue(values, 'wedBillHours'),
          wedCaregiverId: helpers.extraFormSelectValue(values, 'wedCaregiverId'),
          wedContractId: helpers.extraFormSelectValue(values, 'wedContractId'),
          wedServiceId: helpers.extraFormSelectValue(values, 'wedServiceId'),
          wedDisciplineRate: helpers.extraFormInputNumberValue(values, 'wedDisciplineRate'),

          thuFromTime: helpers.extraFormTimeValue(values, 'thuFromTime'),
          thuToTime: helpers.extraFormTimeValue(values, 'thuToTime'),
          thuBillHours: helpers.extraFormInputValue(values, 'thuBillHours'),
          thuCaregiverId: helpers.extraFormSelectValue(values, 'thuCaregiverId'),
          thuContractId: helpers.extraFormSelectValue(values, 'thuContractId'),
          thuServiceId: helpers.extraFormSelectValue(values, 'thuServiceId'),
          thuDisciplineRate: helpers.extraFormInputNumberValue(values, 'thuDisciplineRate'),

          friFromTime: helpers.extraFormTimeValue(values, 'friFromTime'),
          friToTime: helpers.extraFormTimeValue(values, 'friToTime'),
          friBillHours: helpers.extraFormInputValue(values, 'friBillHours'),
          friCaregiverId: helpers.extraFormSelectValue(values, 'friCaregiverId'),
          friContractId: helpers.extraFormSelectValue(values, 'friContractId'),
          friServiceId: helpers.extraFormSelectValue(values, 'friServiceId'),
          friDisciplineRate: helpers.extraFormInputNumberValue(values, 'friDisciplineRate'),

          satFromTime: helpers.extraFormTimeValue(values, 'satFromTime'),
          satToTime: helpers.extraFormTimeValue(values, 'satToTime'),
          satBillHours: helpers.extraFormInputValue(values, 'satBillHours'),
          satCaregiverId: helpers.extraFormSelectValue(values, 'satCaregiverId'),
          satContractId: helpers.extraFormSelectValue(values, 'satContractId'),
          satServiceId: helpers.extraFormSelectValue(values, 'satServiceId'),
          satDisciplineRate: helpers.extraFormInputNumberValue(values, 'satDisciplineRate'),
        };

        if (this.isAdding()) {
          await this.callService('/masterweek/add', params);
          this.notifySuccess('Success', 'A new masterweek has been added.');
          this.onSaveSuccess();
        } else {
          // Before posting, pop up a dialog to ask for the effective date.
          this.setState({
            isAskEffectiveDateDialogVisible: true,
            onAskEffectiveDateDialogConfirmClick: async (effectiveDate) => {
              if (moment(effectiveDate, 'MM/DD/YYYY').isValid()) {
                params.effectiveDate = misc.toDateString(moment(effectiveDate, 'MM/DD/YYYY'));
                await this.callService('/masterweek/update', params);
                this.notifySuccess('Success', 'This masterweek has been updated.');
                this.onSaveSuccess();
              } else {
                this.notifyError('Error', 'Invalid effective date.');
              }
            },
          });
        }
      }
    });
  }

  onCancelClick() {
    const { form, masterweek, onAddCancel } = this.props;

    this.setState({
      addingOrEditing: !masterweek,
    });

    if (this.isAdding()) {
      onAddCancel();
    } else {
      // Reset input values to the original. It will also clear any existing warnings.
      form.resetFields();
      this.onContractSelect(masterweek.getRemoteObject('sunday')?.getLong('contractId'), 'sun');
      this.onContractSelect(masterweek.getRemoteObject('monday')?.getLong('contractId'), 'mon');
      this.onContractSelect(masterweek.getRemoteObject('tuesday')?.getLong('contractId'), 'tue');
      this.onContractSelect(masterweek.getRemoteObject('wednesday')?.getLong('contractId'), 'wed');
      this.onContractSelect(masterweek.getRemoteObject('thursday')?.getLong('contractId'), 'thu');
      this.onContractSelect(masterweek.getRemoteObject('friday')?.getLong('contractId'), 'fri');
      this.onContractSelect(masterweek.getRemoteObject('saturday')?.getLong('contractId'), 'sat');
    }
  }

  onSaveSuccess() {
    this.setState({
      addingOrEditing: !this.props.masterweek,
    });

    if (this.isAdding()) {
      this.props.onAddSuccess();
    } else {
      this.props.onUpdateSuccess();
    }
  }

  onContractSelect(contractId, dayPrefix) {
    this.setState({
      [`${dayPrefix}Services`]: this.findContractServices(contractId),
    }, () => {
      const contract = this.props.contracts.find(c => c.getId() === contractId);
      this.props.form.setFieldsValue({
        [`${dayPrefix}ServiceId`]: contract?.getLong('serviceId'),
      });
    });
  }

  onFromTimeChange(fromTime, dayPrefix) {
    const toTime = this.props.form.getFieldValue(`${dayPrefix}ToTime`);
    this.calcAndSetBillHours(fromTime, toTime, dayPrefix);
  }

  onToTimeChange(toTime, dayPrefix) {
    const fromTime = this.props.form.getFieldValue(`${dayPrefix}FromTime`);
    this.calcAndSetBillHours(fromTime, toTime, dayPrefix);
  }

  // Calculates the duration between two time. Note that, when the toTime <= fromTime,
  // we assume that the toTime has gone to the next day.
  calcAndSetBillHours(fromTime, toTime, dayPrefix) {
    if (fromTime && toTime) {
      const diffInMs = fromTime.isBefore(toTime)
        ? toTime.diff(fromTime)
        : 86400000 - (fromTime.diff(toTime));

      const duration = moment.duration(diffInMs);
      const hours = Math.floor(duration.asHours());
      const minutes = moment.utc(duration.asMilliseconds()).format('mm');
      const billHours = `${hours < 10 ? '0' : ''}${hours}:${minutes}`;

      this.props.form.setFieldsValue({
        [`${dayPrefix}BillHours`]: billHours,
      });
    }
  }

  findContractServices(contractId) {
    const { contracts, servicesByCompany } = this.props;

    const contract = contracts.find(c => c.getId() === contractId);
    return (contract && servicesByCompany[contract.getLong('companyId')]) || [];
  }

  validateOneDay(rule, value, callback, source, options) {
    if (value === undefined || value === null || value === '') {
      const dayPrefix = rule.field.substring(0, 3);

      // Get values of the same day.
      const fieldValues = this.props.form.getFieldsValue(['FromTime', 'ToTime', 'BillHours',
        'CaregiverId', 'ContractId', 'ServiceId', 'DisciplineRate'].map(f => `${dayPrefix}${f}`));

      const fromTime = fieldValues[`${dayPrefix}FromTime`];
      const toTime = fieldValues[`${dayPrefix}ToTime`];
      const billHours = fieldValues[`${dayPrefix}BillHours`];
      const caregiverId = fieldValues[`${dayPrefix}CaregiverId`];
      const contractId = fieldValues[`${dayPrefix}ContractId`];
      const serviceId = fieldValues[`${dayPrefix}ServiceId`];
      const disciplineRate = fieldValues[`${dayPrefix}DisciplineRate`];
      if ((fromTime !== undefined && fromTime !== null && fromTime !== '') ||
          (toTime !== undefined && toTime !== null && toTime !== '') ||
          (billHours !== undefined && billHours !== null && billHours !== '') ||
          (caregiverId !== undefined && caregiverId !== null && caregiverId !== '') ||
          (contractId !== undefined && contractId !== null && contractId !== '') ||
          (serviceId !== undefined && serviceId !== null && serviceId !== '') ||
          (disciplineRate !== undefined && disciplineRate !== null && disciplineRate !== '')) {
        callback('This field should not be empty.');
      } else {
        callback();
      }
    } else if (rule.field.endsWith('BillHours')) {
      if (value && !value.endsWith(':30') && !value.endsWith(':00')) {
        callback('Bill hours can only end with :30 or :00.');
      } else {
        callback();
      }
    } else {
      callback();
    }
  }

  validateEndDate(rule, value, callback, source, options) {
    if (value) {
      // Get start date.
      const startDate = this.props.form.getFieldValue('startDate');

      if (startDate && value.isBefore(startDate, 'day')) {
        callback('End date should not be before start date.');
      } else {
        callback();
      }
    } else {
      callback();
    }
  }

  renderLeftLabels() {
    return (
      <FlexColContainer
        width="120px"
        height="wrap_content"
      >
        <FlexColContainer
          width="match_parent"
          height="35px"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        />

        <FlexColContainer
          width="match_parent"
          height="57px"
          padding="4px 8px"
          justifyContent="center"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            textAlign="right"
            color="textPrimaryDark"
            fontSize="12px"
            fontWeight="bold"
          >
            From Time (00:00 - 23:59)
          </styledComp.Span>
        </FlexColContainer>

        <FlexColContainer
          width="match_parent"
          height="57px"
          padding="4px 8px"
          justifyContent="center"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            textAlign="right"
            color="textPrimaryDark"
            fontSize="12px"
            fontWeight="bold"
          >
            To Time (00:00 - 23:59)
          </styledComp.Span>
        </FlexColContainer>

        <FlexColContainer
          width="match_parent"
          height="57px"
          padding="4px 8px"
          justifyContent="center"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            textAlign="right"
            color="textPrimaryDark"
            fontSize="12px"
            fontWeight="bold"
          >
            Bill Hours (00:00 - 23:59)
          </styledComp.Span>
        </FlexColContainer>

        <FlexColContainer
          width="match_parent"
          height="57px"
          padding="4px 8px"
          justifyContent="center"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            textAlign="right"
            color="textPrimaryDark"
            fontSize="12px"
            fontWeight="bold"
          >
            Caregivers
          </styledComp.Span>
        </FlexColContainer>

        <FlexColContainer
          width="match_parent"
          height="57px"
          padding="4px 8px"
          justifyContent="center"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            textAlign="right"
            color="textPrimaryDark"
            fontSize="12px"
            fontWeight="bold"
          >
            Bill to Contract
          </styledComp.Span>
        </FlexColContainer>

        <FlexColContainer
          width="match_parent"
          height="57px"
          padding="4px 8px"
          justifyContent="center"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            textAlign="right"
            color="textPrimaryDark"
            fontSize="12px"
            fontWeight="bold"
          >
            Service Code
          </styledComp.Span>
        </FlexColContainer>

        <FlexColContainer
          width="match_parent"
          height="57px"
          padding="4px 8px"
          justifyContent="center"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            textAlign="right"
            color="textPrimaryDark"
            fontSize="12px"
            fontWeight="bold"
          >
            Discipline Rate ($)
          </styledComp.Span>
        </FlexColContainer>
      </FlexColContainer>
    );
  }

  renderOneDay(day, dayPrefix, masterweekDay) {
    const { form, caregivers, contracts } = this.props;
    const { addingOrEditing } = this.state;

    return (
      <FlexColContainer
        width="0"
        height="wrap_content"
        flexGrow="1"
      >
        <FlexColContainer
          width="match_parent"
          height="wrap_content"
          padding="8px 8px"
          alignItems="center"
          backgroundColor="#ffffff"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <styledComp.Span
            width="wrap_content"
            height="wrap_content"
            color="textPrimaryDark"
            fontSize="12px"
            fontWeight="bold"
          >
            {day}
          </styledComp.Span>
        </FlexColContainer>

        {/* From time */}
        <FlexColContainer
          width="match_parent"
          height="wrap_content"
          padding="8px 8px"
          backgroundColor="#f9f9f9"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <FormTimePicker
            width="match_parent"
            height="wrap_content"
            margin="0"
            form={form}
            name={`${dayPrefix}FromTime`}
            placeholder="hh:mm"
            disabled={!addingOrEditing}
            help=""
            initialValue={masterweekDay?.getString('fromTimeString') &&
              moment(masterweekDay.getString('fromTimeString'), 'HH:mm')}
            validator={this.validateOneDay}
            onChange={time => this.onFromTimeChange(time, dayPrefix)}
          />
        </FlexColContainer>

        {/* To time */}
        <FlexColContainer
          width="match_parent"
          height="wrap_content"
          padding="8px 8px"
          backgroundColor="#ffffff"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <FormTimePicker
            width="match_parent"
            height="wrap_content"
            margin="0"
            form={form}
            name={`${dayPrefix}ToTime`}
            placeholder="hh:mm"
            disabled={!addingOrEditing}
            help=""
            initialValue={masterweekDay?.getString('toTimeString') &&
              moment(masterweekDay.getString('toTimeString'), 'HH:mm')}
            validator={this.validateOneDay}
            onChange={time => this.onToTimeChange(time, dayPrefix)}
          />
        </FlexColContainer>

        {/* Bill hours */}
        <FlexColContainer
          width="match_parent"
          height="wrap_content"
          padding="8px 8px"
          backgroundColor="#f9f9f9"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <FormInput
            width="match_parent"
            height="wrap_content"
            margin="0"
            form={form}
            name={`${dayPrefix}BillHours`}
            placeholder=""
            max={30}
            disabled={!addingOrEditing}
            help=""
            initialValue={masterweekDay?.getString('billHoursString')}
            validator={this.validateOneDay}
          />
        </FlexColContainer>

        {/* Caregivers */}
        <FlexColContainer
          width="match_parent"
          height="wrap_content"
          padding="8px 8px"
          backgroundColor="#ffffff"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <FormSelect2
            width="match_parent"
            height="wrap_content"
            margin="0"
            fontSize="10px"
            form={form}
            name={`${dayPrefix}CaregiverId`}
            placeholder="Select..."
            disabled={!addingOrEditing}
            help=""
            options={caregivers}
            showIdAfterName
            initialValue={masterweekDay?.getLong('caregiverId')}
            validator={this.validateOneDay}
          />
        </FlexColContainer>

        {/* Contracts */}
        <FlexColContainer
          width="match_parent"
          height="wrap_content"
          padding="8px 8px"
          backgroundColor="#f9f9f9"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <FormSelect
            width="match_parent"
            height="wrap_content"
            margin="0"
            fontSize="10px"
            form={form}
            name={`${dayPrefix}ContractId`}
            placeholder="Select..."
            disabled={!addingOrEditing}
            help=""
            options={contracts.map(c => ({
              id: c.getId(),
              name: c.getString('fullName'),
            }))}
            initialValue={masterweekDay?.getLong('contractId')}
            validator={this.validateOneDay}
            onSelect={contractId => this.onContractSelect(contractId, dayPrefix)}
          />
        </FlexColContainer>

        {/* Service code */}
        <FlexColContainer
          width="match_parent"
          height="wrap_content"
          padding="8px 8px"
          backgroundColor="#ffffff"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <FormSelect
            width="match_parent"
            height="wrap_content"
            margin="0"
            fontSize="10px"
            form={form}
            name={`${dayPrefix}ServiceId`}
            placeholder="Select..."
            disabled={!addingOrEditing}
            help=""
            options={this.state[`${dayPrefix}Services`].map(s => ({
              id: s.id,
              name: `${s.displayCode} - ${s.unitType}`,
            }))}
            initialValue={masterweekDay?.getLong('serviceId')}
            validator={this.validateOneDay}
          />
        </FlexColContainer>

        {/* Discipline rate */}
        <FlexColContainer
          width="match_parent"
          height="wrap_content"
          padding="8px 8px"
          backgroundColor="#f9f9f9"
          borderRight="1px solid controlBorder"
          borderBottom="1px solid controlBorder"
        >
          <FormInputNumber
            width="match_parent"
            height="wrap_content"
            margin="0"
            form={form}
            name={`${dayPrefix}DisciplineRate`}
            placeholder="0.00"
            disabled={!addingOrEditing}
            min={0.00}
            step={0.01}
            precision={2}
            help=""
            initialValue={masterweekDay?.getDouble('disciplineRate')}
            validator={this.validateOneDay}
          />
        </FlexColContainer>
      </FlexColContainer>
    );
  }

  renderContent() {
    // eslint-disable-block no-unused-vars
    const {
      form,
      masterweek,
      onAddSuccess,
      onAddCancel,
      onDeleteSuccess,
      onUpdateSuccess,
      ...layoutProps
    } = this.props;

    const {
      addingOrEditing,
      isAskEffectiveDateDialogVisible,
      onAskEffectiveDateDialogConfirmClick,
    } = this.state;

    return (
      <FlexColContainer
        {...layoutProps}
      >
        <styledComp.Span
          width="wrap_content"
          height="wrap_content"
          margin="30px 0"
          color="textPrimaryDark"
          fontSize="24px"
          fontWeight="700"
        >
          {`Masterweek: 
          ${masterweek?.getString('startDateUsString') || ''} - 
          ${masterweek?.getString('endDateUsString') || ''}`}
        </styledComp.Span>

        <FlexRowContainer
          width="match_parent"
          height="wrap_content"
          margin="0 0 24px 0"
          borderTop="1px solid controlBorder"
          borderLeft="1px solid controlBorder"
        >
          {this.renderLeftLabels()}
          {this.renderOneDay('Sunday', 'sun', masterweek?.getRemoteObject('sunday'))}
          {this.renderOneDay('Monday', 'mon', masterweek?.getRemoteObject('monday'))}
          {this.renderOneDay('Tuesday', 'tue', masterweek?.getRemoteObject('tuesday'))}
          {this.renderOneDay('Wednesday', 'wed', masterweek?.getRemoteObject('wednesday'))}
          {this.renderOneDay('Thursday', 'thu', masterweek?.getRemoteObject('thursday'))}
          {this.renderOneDay('Friday', 'fri', masterweek?.getRemoteObject('friday'))}
          {this.renderOneDay('Saturday', 'sat', masterweek?.getRemoteObject('saturday'))}
        </FlexRowContainer>

        <FlexColContainer
          width="match_parent"
          height="wrap_content"
          padding="20px 20px"
          justifyContent="center"
          alignItems="center"
          backgroundColor="containerGrayBackground"
        >
          <FlexRowContainer
            width="match_parent"
            height="wrap_content"
            margin="0 0 24px 0"
            justifyContent="center"
            alignItems="flex-start"
          >
            <FormDatePicker
              width="200px"
              height="wrap_content"
              margin="0 32px 0 0"
              form={form}
              name="startDate"
              label="Start Date (Inclusive)"
              placeholder="mm/dd/yyyy"
              disabled={!addingOrEditing}
              initialValue={masterweek?.getMoment('startDate')}
              isRequired
            />

            <FormDatePicker
              width="200px"
              height="wrap_content"
              margin="0 64px 0 0"
              form={form}
              name="endDate"
              label="End Date (Inclusive)"
              placeholder="mm/dd/yyyy"
              disabled={!addingOrEditing}
              initialValue={masterweek
                ? masterweek.getMoment('endDate')
                : moment('12/31/2099', 'MM/DD/YYYY')
              }
              isRequired
              validator={this.validateEndDate}
            />

            {!addingOrEditing &&
              <CustomButton
                width="wrap_content"
                height="32px"
                margin="28px 64px 0 0"
                backgroundColor="blue_steel"
                fontSize="14px"
                type="primary"
                onClick={this.onEditClick}
              >
                EDIT MASTERWEEK
              </CustomButton>
            }

            {!addingOrEditing &&
              <Popconfirm
                title="Are you sure you want to delete this masterweek?"
                placement="top"
                okText="Confirm"
                cancelText="Cancel"
                onConfirm={this.onDeleteClick}
              >
                <styledComp.AntdButton
                  width="wrap_content"
                  height="32px"
                  margin="28px 0 0 0"
                  fontSize="14px"
                  type="danger"
                >
                  DELETE MASTERWEEK
                </styledComp.AntdButton>
              </Popconfirm>
            }

            {addingOrEditing &&
              <CustomButton
                width="wrap_content"
                height="32px"
                margin="28px 64px 0 0"
                backgroundColor="blue_steel"
                fontSize="14px"
                type="primary"
                onClick={this.onSaveClick}
              >
                SAVE MASTERWEEK
              </CustomButton>
            }

            {addingOrEditing &&
              <styledComp.AntdButton
                width="wrap_content"
                height="32px"
                margin="28px 0 0 0"
                fontSize="14px"
                type="default"
                onClick={this.onCancelClick}
              >
                CANCEL
              </styledComp.AntdButton>
            }
          </FlexRowContainer>

          <FlexColContainer
            width="wrap_content"
            height="wrap_content"
          >
            <styledComp.Span
              width="wrap_content"
              height="wrap_content"
              color="textPrimaryDark"
              fontSize="14px"
            >
              1. New schedules will be auto-generated by the masterweek for up to 14 days in
              advanced at 2:00AM everyday.
            </styledComp.Span>

            <styledComp.Span
              width="wrap_content"
              height="wrap_content"
              color="textPrimaryDark"
              fontSize="14px"
            >
              2. Modifying a masterweek will delete all unconfirmed schedules on and after the
              effective date. New schedules will be generated at 2:00AM tomorrow.
            </styledComp.Span>
          </FlexColContainer>
        </FlexColContainer>

        <AskEffectiveDateDialog
          width="wrap_content"
          height="wrap_content"
          isVisible={isAskEffectiveDateDialogVisible}
          onVisibleChange={visible => this.setState({ isAskEffectiveDateDialogVisible: visible })}
          onConfirmClick={onAskEffectiveDateDialogConfirmClick}
        />
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
PatientMasterweek.propTypes = propTypes;

export default Form.create()(PatientMasterweek);
